import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import CasinoIcon from '@mui/icons-material/Casino';
import { GiftProduct, GiftProductType, IntegrationType } from '../../../models';
import { ImageWithFallback } from '../../../components';
import { formatNumber } from '../../../utils';
import { __t } from '../../../i18n/t';

interface ProductListProps {
  products: GiftProduct[];
  onAdd: () => void;
  onEdit: (product: GiftProduct, index: number) => void;
  onDelete: (index: number) => void;
  fuels?: any[];
}

const ProductList: React.FC<ProductListProps> = ({
  products,
  onAdd,
  onEdit,
  onDelete,
  fuels = [],
}) => {
  const getProductTypeLabel = (product: GiftProduct): { label: string; color: string; bgColor: string } => {
    // Recompensa em dinheiro é produto virtual no banco, mas quem cadastrou escolheu "Cashback":
    // mostrar "Produto Virtual" aqui faria a lista contradizer o seletor.
    if ((product.cashValue ?? null) !== null) {
      return { label: 'Cashback', color: '#2e7d32', bgColor: '#e8f5e9' };
    }

    switch (product.type) {
      case GiftProductType.Virtual:
        return { label: 'Produto Virtual', color: '#1976d2', bgColor: '#e3f2fd' };
      case GiftProductType.Existing:
        return { label: 'Produto Existente', color: '#7b1fa2', bgColor: '#f3e5f5' };
      case GiftProductType.FuelDiscount:
        return { label: 'Desconto Abastecimento', color: '#f57c00', bgColor: '#fff3e0' };
      case GiftProductType.PredictionTicket:
        return { label: 'Direito ao Palpite (Bolão)', color: '#7869DF', bgColor: '#ede9fe' };
      case GiftProductType.DrawingNumber:
        return { label: 'Número da Sorte (Sorteio)', color: '#00897b', bgColor: '#e0f2f1' };
      case GiftProductType.SpinTicket:
        return { label: 'Giro na Roleta', color: '#7869DF', bgColor: '#ede9fe' };
      default:
        return { label: 'Produto', color: '#666', bgColor: '#f5f5f5' };
    }
  };

  const getProductName = (product: GiftProduct): string => {
    if (product.type === GiftProductType.Virtual) {
      return product.productName || 'Sem nome';
    } else if (product.type === GiftProductType.Existing) {
      return product.productName || `Produto ID: ${product.productId}`;
    } else if (product.type === GiftProductType.FuelDiscount) {
      return 'Desconto no Combustível';
    } else if (product.type === GiftProductType.PredictionTicket) {
      return `Direito ao palpite${product.bolaoName ? `: ${product.bolaoName}` : ''}`;
    } else if (product.type === GiftProductType.DrawingNumber) {
      return `Número da sorte${product.drawingName ? `: ${product.drawingName}` : ''}`;
    } else if (product.type === GiftProductType.SpinTicket) {
      return `Giro na roleta${product.roletaName ? `: ${product.roletaName}` : ''}`;
    }
    return 'Produto';
  };

  const getProductDetails = (product: GiftProduct): string => {
    if (product.type === GiftProductType.Virtual) {
      if (product.cashValue != null) {
        // Frase montada fora de JSX: o plugin de i18n não embrulha, então a chave é à mão
        // (a entrada em espanhol vive em i18n/messages/es/catalog/base.ts).
        return __t('Vale %{value} — resgatável em qualquer local que aceite cashback', {
          value: formatNumber(product.cashValue, { style: 'currency', currency: 'BRL' }),
        });
      }
      const details: string[] = [];
      if (product.productValue) {
        details.push(`Valor: ${formatNumber(product.productValue, { style: 'currency', currency: 'BRL' })}`);
      }
      if (product.discountPercentage) {
        details.push(`Desconto: ${product.discountPercentage}%`);
      }
      return details.join(' • ');
    } else if (product.type === GiftProductType.Existing) {
      return product.productValue ? `Valor: ${formatNumber(product.productValue, { style: 'currency', currency: 'BRL' })}` : 'Produto do catálogo';
    } else if (product.type === GiftProductType.FuelDiscount) {
      return product.discountId ? 'Vinculado a desconto de recompensa' : 'Sem desconto vinculado';
    } else if (product.type === GiftProductType.PredictionTicket) {
      return product.bolaoId ? 'Ticket de palpite — sem cupom direto' : 'Bolão não selecionado';
    } else if (product.type === GiftProductType.DrawingNumber) {
      const amount = product.luckyNumberAmount ?? 1;
      return product.drawingId
        ? `${amount} ${amount === 1 ? 'número' : 'números'} por conquista — sem cupom`
        : 'Sorteio não selecionado';
    } else if (product.type === GiftProductType.SpinTicket) {
      const amount = product.spinAmount ?? 1;
      return product.roletaId
        ? `${amount} ${amount === 1 ? 'giro' : 'giros'} por conquista — sem cupom`
        : 'Roleta não selecionada';
    }
    return '';
  };

  const getProductImage = (product: GiftProduct): string | null => {
    if (product.type === GiftProductType.Virtual || product.type === GiftProductType.Existing) {
      return product.productImagePath || null;
    }
    return null;
  };

  const getProductFallbackIcon = (type: GiftProductType) => {
    switch (type) {
      case GiftProductType.Virtual:
        return <CardGiftcardIcon sx={{ fontSize: 28, color: '#1976d2' }} />;
      case GiftProductType.Existing:
        return <InventoryIcon sx={{ fontSize: 28, color: '#7b1fa2' }} />;
      case GiftProductType.FuelDiscount:
        return <LocalGasStationIcon sx={{ fontSize: 28, color: '#f57c00' }} />;
      case GiftProductType.PredictionTicket:
        return <SportsSoccerIcon sx={{ fontSize: 28, color: '#7869DF' }} />;
      case GiftProductType.DrawingNumber:
        return <CasinoIcon sx={{ fontSize: 28, color: '#00897b' }} />;
      case GiftProductType.SpinTicket:
        return <CasinoIcon sx={{ fontSize: 28, color: '#7869DF' }} />;
      default:
        return null;
    }
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} mb={2}>
        Produtos do Brinde
      </Typography>

      {products.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            mb: 2,
          }}
        >
          <Typography color="text.secondary" gutterBottom>
            Nenhum produto adicionado
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Clique no botão abaixo para adicionar o primeiro produto
          </Typography>
        </Paper>
      ) : (
        <Box display="flex" flexDirection="column" gap={1.5} mb={2}>
          {products.map((product, index) => {
            const typeInfo = getProductTypeLabel(product);
            return (
              <Paper
                key={product.id || index}
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                  <Box display="flex" alignItems="center" gap={2} flex={1}>
                    <ImageWithFallback
                      src={getProductImage(product)}
                      alt={getProductName(product)}
                      width={60}
                      height={60}
                      borderRadius={8}
                      fallbackIcon={getProductFallbackIcon(product.type)}
                    />
                    <Box flex={1}>
                      <Box display="flex" gap={1} mb={1}>
                        <Chip
                          label={typeInfo.label}
                          size="small"
                          sx={{
                            bgcolor: typeInfo.bgColor,
                            color: typeInfo.color,
                            fontWeight: 600,
                          }}
                        />
                        {product.integrationType && (
                          <Chip
                            label={IntegrationType.translateType(product.integrationType)}
                            size="small"
                            sx={{
                              bgcolor: product.integrationType === IntegrationType.rti ? '#e8f5e9' : '#fff3e0',
                              color: product.integrationType === IntegrationType.rti ? '#2e7d32' : '#e65100',
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="body1" fontWeight={600}>
                        {getProductName(product)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getProductDetails(product)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" gap={1}>
                    <IconButton
                      size="small"
                      onClick={() => onEdit(product, index)}
                      sx={{
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => onDelete(index)}
                      sx={{
                        '&:hover': {
                          bgcolor: 'error.lighter',
                          color: 'error.main',
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      <Button
        fullWidth
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={onAdd}
        sx={{
          py: 2,
          borderWidth: 2,
          borderStyle: 'dashed',
          '&:hover': {
            borderWidth: 2,
            borderColor: 'primary.main',
            bgcolor: 'primary.lighter',
          },
        }}
      >
        Adicionar Produto
      </Button>
    </Box>
  );
};

export default ProductList;
